import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import OptimizedImage from '@/components/OptimizedImage';
import { API } from '@/App';
import axios from 'axios';
import { Calendar, Tag } from 'lucide-react';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?active_only=true`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(projects.map(p => p.category))];
  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>Projelerimiz - Ener Yazılım | Başarılı Proje Portföyü</title>
        <meta name="description" content="Gerçekleştirdiğimiz başarılı projelerle işletmelere değer katıyoruz. E-ticaret, AI, kurumsal yazılım ve daha fazlası." />
        <meta name="keywords" content="projeler, portföy, başarılı projeler, e-ticaret, AI projeler, kurumsal yazılım" />
        <link rel="canonical" href="https://www.eneryazilim.com/projeler" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/projeler" />
        <meta property="og:title" content="Projelerimiz - Ener Yazılım" />
        <meta property="og:description" content="Gerçekleştirdiğimiz başarılı projelerle işletmelere değer katıyoruz." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/projeler" />
        <meta name="twitter:title" content="Projelerimiz - Ener Yazılım" />
        <meta name="twitter:description" content="Gerçekleştirdiğimiz başarılı projelerle işletmelere değer katıyoruz." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - ItemList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Ener Yazılım Projeleri",
            "description": "Gerçekleştirdiğimiz başarılı projeler",
            "itemListElement": projects.slice(0, 10).map((project, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "CreativeWork",
                "name": project.title,
                "description": project.description
              }
            }))
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="projects-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="projects-title">
              <span className="gradient-text">Projelerimiz</span>
            </h1>
            <p className="text-lg text-white/70">
              Gerçekleştirdiğimiz başarılı projelerle işletmelere değer katıyoruz
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      {categories.length > 1 && (
        <section className="pb-8" data-testid="projects-filter">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  data-testid={`filter-${category}`}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white'
                      : 'glass text-white/70 hover:bg-white/10'
                  }`}
                >
                  {category === 'all' ? 'Tümü' : category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Projects Grid */}
      <section className="pb-20" data-testid="projects-grid">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-white/60">Yükleniyor...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center text-white/60" data-testid="no-projects">Henüz proje eklenmemiş.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  data-testid={`project-${project.id}`}
                  className="glass rounded-2xl overflow-hidden card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {project.image_url ? (
                    <OptimizedImage
                      src={project.image_url}
                      alt={project.title}
                      className="h-48"
                      loading="lazy"
                      objectFit="cover"
                    />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20 flex items-center justify-center">
                      <div className="text-6xl opacity-50">💻</div>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <Tag className="w-4 h-4 text-[#FF6B35]" />
                      <span className="text-sm text-[#FF6B35]">{project.category}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                    <p className="text-sm text-white/60 mb-3">{project.client}</p>
                    <p className="text-sm text-white/70 mb-4">{project.description}</p>
                    {project.completion_date && (
                      <div className="flex items-center space-x-2 text-xs text-white/50 mb-4">
                        <Calendar className="w-4 h-4" />
                        <span>{project.completion_date}</span>
                      </div>
                    )}
                    {project.technologies && project.technologies.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, i) => (
                          <span key={i} className="text-xs px-3 py-1 bg-white/5 rounded-full">
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;